/**
 * InviteMemberSlideOver — phone-based team invite form.
 *
 * Mirrors the mobile invite flow:
 *   - Phone number (E.164, auto-normalised for IN numbers)
 *   - Display name (optional, shown in pending list)
 *   - Role chip picker (admin / manager / accountant / etc.)
 *   - Project access picker — for non-admin roles only. Admins,
 *     managers, accountants see EVERY project by virtue of their
 *     org role, so no scoping is needed. Site-engineer / supervisor /
 *     designer / viewer / client all get scoped to a chosen subset.
 *
 * On submit calls the `inviteMember` Cloud Function which:
 *   - If the phone matches an existing user: adds them immediately +
 *     refreshes their custom claims
 *   - Otherwise: writes a pending invite at
 *     `organizations/{orgId}/pendingInvites/{phone}` claimed on
 *     their first sign-in
 */
import { useEffect, useMemo, useState } from 'react';

import { assignableRolesFor, ROLE_DESCRIPTIONS } from '@shared/perms';

import {
  inviteProjectMember,
  normalizeIndianPhoneE164,
} from '@/lib/teamMembers';
import {
  ROLE_LABELS,
  type AssignableRole,
} from '@/lib/useProjectMembers';
import { ROLE_TONE } from '@/lib/useOrgMembers';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useProjects } from '@/lib/useProjects';

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};
const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

/** Roles that get every project automatically (server enforces). */
const UNSCOPED_ROLES = new Set<AssignableRole>([
  'admin',
  'manager',
  'accountant',
]);

export type InviteMemberSlideOverProps = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  onInvited?: (result: { joinedNow: boolean; uid?: string }) => void;
};

export function InviteMemberSlideOver({
  open,
  onClose,
  orgId,
  onInvited,
}: InviteMemberSlideOverProps) {
  const { projects } = useProjects();
  const { role: callerRole } = useCurrentOrgRole();

  // Narrow the chip list to roles the caller is actually allowed to
  // assign — Manager can't grant Admin/SA/another Manager, Admin
  // can't grant Admin/SA, and roles below Manager don't open this
  // slide-over at all (parent gate hides the button).
  const assignableRoles = useMemo(
    () => assignableRolesFor(callerRole),
    [callerRole],
  );

  // Default role = first chip in the narrowed list, falls back to
  // 'manager' for callers with no list (parent should hide the
  // button in that case, but be defensive).
  const defaultRole: AssignableRole = assignableRoles[0] ?? 'manager';

  const [phone, setPhone] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<AssignableRole>(defaultRole);
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(
    () => new Set(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPhone('');
    setDisplayName('');
    setRole(defaultRole);
    setSelectedProjects(new Set());
    setBusy(false);
    setError(null);
    setSuccess(null);
  }, [open, defaultRole]);

  // Body scroll-lock + Esc.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const needsProjectScope = !UNSCOPED_ROLES.has(role);
  const isClient = role === 'client';

  const sortedProjects = useMemo(
    () =>
      [...projects].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [projects],
  );

  function toggleProject(id: string) {
    setSelectedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSubmit() {
    setError(null);
    setSuccess(null);
    if (!orgId) return setError('No organisation context.');
    const phoneE164 = normalizeIndianPhoneE164(phone);
    if (!phoneE164) {
      return setError(
        'Enter a valid phone number (10 digits, or full +country format).',
      );
    }
    if (isClient && selectedProjects.size === 0) {
      return setError(
        'Clients must be scoped to at least one project — pick one below.',
      );
    }

    setBusy(true);
    try {
      const projectIds = needsProjectScope
        ? Array.from(selectedProjects)
        : projects.map((p) => p.id); // server enforces "all" for admin/manager/accountant; we still pass the full list as a hint.
      const result = await inviteProjectMember({
        orgId,
        phoneNumber: phoneE164,
        role,
        projectIds,
        displayName: displayName.trim() || undefined,
      });
      onInvited?.(result);
      if (result.joinedNow) {
        setSuccess(
          `Added to your studio — they already had an InteriorIO account.`,
        );
      } else {
        setSuccess(
          `Invite sent. They'll join the studio when they sign in with ${phoneE164}.`,
        );
      }
      // Reset form for the next invite. Keep the slide-over open so
      // the user can fire several in a row.
      setPhone('');
      setDisplayName('');
      setSelectedProjects(new Set());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-user-plus" /> Add team member
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              style={{
                background: '#FEF2F2',
                color: 'var(--critical)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
                border: '1px solid #FECACA',
              }}
            >
              {error}
            </div>
          ) : null}

          {success ? (
            <div
              style={{
                background: '#F0FDF4',
                color: 'var(--success)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
                border: '1px solid #BBF7D0',
              }}
            >
              {success}
            </div>
          ) : null}

          <div>
            <label style={LABEL}>Phone *</label>
            <input
              style={INPUT}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.currentTarget.value)}
              placeholder="98765 43210"
              autoFocus
              maxLength={20}
              autoComplete="off"
            />
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: 'var(--text-tertiary)',
              }}
            >
              We'll prefix +91 automatically for Indian numbers.
            </div>
          </div>

          <div>
            <label style={LABEL}>Display name (optional)</label>
            <input
              style={INPUT}
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              placeholder="e.g. Ramesh Kumar"
              maxLength={60}
              autoComplete="off"
            />
          </div>

          {/* Role chip picker */}
          <div>
            <label style={LABEL}>Role *</label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 6,
              }}
            >
              {assignableRoles.map((r) => {
                const active = r === role;
                const t = ROLE_TONE[r];
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    style={{
                      padding: '8px 10px',
                      border: active
                        ? `1.5px solid ${t.fg}`
                        : '1px solid var(--border)',
                      background: active ? t.bg : '#fff',
                      color: active ? t.fg : 'var(--text-secondary)',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                    }}
                  >
                    {ROLE_LABELS[r]}
                  </button>
                );
              })}
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: 'var(--text-tertiary)',
                lineHeight: 1.4,
              }}
            >
              {ROLE_DESCRIPTIONS[role]}
            </div>
          </div>

          {/* Project scope (only for roles that need it) */}
          {needsProjectScope ? (
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 6,
                }}
              >
                <label style={{ ...LABEL, marginBottom: 0 }}>
                  Projects {isClient ? '*' : ''}
                </label>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-tertiary)',
                  }}
                >
                  {selectedProjects.size} of {projects.length} selected
                </span>
              </div>
              {projects.length === 0 ? (
                <div
                  style={{
                    padding: 12,
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    border: '1px dashed var(--border)',
                    borderRadius: 6,
                  }}
                >
                  No projects yet. Create one first to scope this member.
                </div>
              ) : (
                <div
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    maxHeight: 200,
                    overflowY: 'auto',
                  }}
                >
                  {sortedProjects.map((p) => {
                    const sel = selectedProjects.has(p.id);
                    return (
                      <label
                        key={p.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          borderTop:
                            sortedProjects[0].id === p.id
                              ? 'none'
                              : '1px solid var(--border)',
                          cursor: 'pointer',
                          background: sel ? 'var(--brand-bg)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={sel}
                          onChange={() => toggleProject(p.id)}
                          style={{ accentColor: 'var(--brand)' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {p.name}
                          </div>
                          {p.location ? (
                            <div
                              style={{
                                fontSize: 11,
                                color: 'var(--text-tertiary)',
                              }}
                            >
                              {p.location}
                            </div>
                          ) : null}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                padding: 10,
                border: '1px dashed var(--border)',
                borderRadius: 6,
              }}
            >
              {ROLE_LABELS[role]}s get access to every project automatically.
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              {success ? 'Done' : 'Cancel'}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
